import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Search as SearchIcon, Sparkles } from "lucide-react";
import { API_BASE_URL, COURSE_DETAILS_FILE } from './config';
import { useSemester } from './SemesterContext';
import { supabase } from "./supabaseClient";
import CoursePopup from "./CoursePopup"; // Add this import

export default function CourseInput({ onHighlight, onConflicted, currentSemester, highlighted }) {
  const [input, setInput] = useState("");
  const [allCourses, setAllCourses] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [useSemanticSearch, setUseSemanticSearch] = useState(false); // toggle state
  const [useAllSemestersSearch, setAllSemestersSearch] = useState(false);
  const { selectedSemester } = useSemester();
  const [selectedCourse, setSelectedCourse] = useState(null); // Add this state
  const [searchError, setSearchError] = useState(null);


  // Helper function to calculate semester distance
  const calculateSemesterDistance = (semester1, semester2) => {
    // Parse semester format: YYMMT (e.g., "2324S")
    const parseSemester = (sem) => {
      const year = parseInt(sem.substring(0, 2) + sem.substring(2, 4)); // Convert YYMM to full year
      const term = sem.charAt(4);
      const termOrder = { 'F': 0, 'J': 1, 'S': 2 }; // Fall, January, Spring
      return { year, termOrder: termOrder[term] || 0 };
    };

    const sem1 = parseSemester(semester1);
    const sem2 = parseSemester(semester2);

    // Calculate distance: (year difference * 3) + term difference
    const yearDiff = Math.abs(sem1.year - sem2.year);
    const termDiff = Math.abs(sem1.termOrder - sem2.termOrder);

    return (yearDiff * 3) + termDiff;
  };

  // Load course data on component mount
  useEffect(() => {
    const loadCourses = async () => {
      try {
        //console.log('Attempting to load courses...');
        const response = await fetch(`/${COURSE_DETAILS_FILE}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        //console.log('Loaded courses:', data.length);
        //console.log('First course:', data[0]);
        setAllCourses(data);
      } catch (error) {
        console.error('Error loading courses:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
      }
    };
    loadCourses();
  }, []);

  // Search function to find matching courses with all semesters listed
  const searchCourses = (searchTerm) => {
    //console.log('Searching with term:', searchTerm);
    //console.log('Current semester:', currentSemester);
    //console.log('Number of courses available:', allCourses.length);

    if (!searchTerm.trim()) {
      setSuggestions([]);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();

    let allMatches;

    // First, find all matching courses across all semesters
    allMatches = allCourses
      .filter(course => {
        if (!course.semester) return false;

        //console.log("current semester is ", selectedSemester)
        if (!useAllSemestersSearch) {
          // Only include courses that match the selected semester
          if (course.semester !== selectedSemester) return false;
        }

        const courseCodes = Array.isArray(course.course_codes)
          ? course.course_codes
          : [course.course_codes];

        const codeMatch = courseCodes.some(code =>
          code.toLowerCase().includes(searchTermLower)
        );
        const titleMatch = course.course_title &&
          course.course_title.toLowerCase().includes(searchTermLower);

        return codeMatch || titleMatch;
      })
      .map(course => ({
        ...course,
        semesterDistance: calculateSemesterDistance(currentSemester, course.semester)
      }));

    // Group courses by their primary course code
    const courseGroups = new Map();

    for (const course of allMatches) {
      const courseCodes = Array.isArray(course.course_codes) ? course.course_codes : [course.course_codes];
      const primaryCode = courseCodes[0];

      if (!courseGroups.has(primaryCode)) {
        courseGroups.set(primaryCode, {
          course_codes: course.course_codes,
          course_title: course.course_title,
          semesters: [],
          courses: [] // Store all course instances
        });
      }

      const group = courseGroups.get(primaryCode);
      group.semesters.push(course.semester);
      group.courses.push(course);
    }

    // Convert grouped courses back to array format with semester lists
    const groupedCourses = Array.from(courseGroups.values()).map(group => {
      // Sort semesters chronologically (you may need to adjust this based on your semester format)
      const sortedSemesters = group.semesters.sort((a, b) => {
        // Assuming semester format like "Fall 2023", "Spring 2024", etc.
        // You may need to adjust this sorting logic based on your actual semester format
        return a.localeCompare(b);
      });

      // Find the course instance from the closest semester for other properties
      const closestCourse = group.courses.reduce((closest, current) => {
        return current.semesterDistance < closest.semesterDistance ? current : closest;
      });

      return {
        ...closestCourse,
        semesters: sortedSemesters,
        semesterCount: sortedSemesters.length,
        // Add a display property for all semesters
        allSemestersText: sortedSemesters.join(', ')
      };
    });

    // Sort by relevance first, then by semester distance of the closest occurrence
    const sortedMatches = groupedCourses.sort((a, b) => {
      // First, prioritize exact matches in course codes
      const aCodes = Array.isArray(a.course_codes) ? a.course_codes : [a.course_codes];
      const bCodes = Array.isArray(b.course_codes) ? b.course_codes : [b.course_codes];

      const aExactMatch = aCodes.some(code => code.toLowerCase() === searchTermLower);
      const bExactMatch = bCodes.some(code => code.toLowerCase() === searchTermLower);

      if (aExactMatch && !bExactMatch) return -1;
      if (!aExactMatch && bExactMatch) return 1;

      // Then prioritize exact matches in titles
      const aTitleExact = a.course_title && a.course_title.toLowerCase() === searchTermLower;
      const bTitleExact = b.course_title && b.course_title.toLowerCase() === searchTermLower;

      if (aTitleExact && !bTitleExact) return -1;
      if (!aTitleExact && bTitleExact) return 1;

      // Then prioritize starts-with matches
      const aStartsWith = aCodes.some(code => code.toLowerCase().startsWith(searchTermLower)) ||
        (a.course_title && a.course_title.toLowerCase().startsWith(searchTermLower));
      const bStartsWith = bCodes.some(code => code.toLowerCase().startsWith(searchTermLower)) ||
        (b.course_title && b.course_title.toLowerCase().startsWith(searchTermLower));

      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;

      // Finally, sort by semester distance (closer semesters first)
      return a.semesterDistance - b.semesterDistance;
    });

    // Limit to 5 suggestions
    const matches = sortedMatches.slice(0, 5);

    //console.log('Found matches with all semesters:', matches);
    setSuggestions(matches);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    setSearchError(null);

    if (!useSemanticSearch) {
      // Default behavior: live search as usual
      searchCourses(value);
      setShowSuggestions(true);
    } else {
      // In semantic mode: no live search
      setShowSuggestions(false);
    }
  };

  const handleToggle = () => {
    setUseSemanticSearch(prev => !prev);
    setSearchError(null);
  };

  useEffect(() => {
    //console.log("useSemanticSearch is now:", useSemanticSearch);
  }, [selectedSemester, useSemanticSearch]);


  const searchSemanticCourse = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSuggestions([]); setSearchError(null);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${backendUrl}/semantic_course_search`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : ""
        },
        body: JSON.stringify({ query: searchTerm, allSemesterSearch: useAllSemestersSearch, currentSemester: selectedSemester }),
      });
      if (!response.ok) {
        // Most commonly: the local backend has no vector DB (Qdrant) running.
        throw new Error(`Semantic search unavailable (${response.status})`);
      }
      const data = await response.json();
      setSearchError(null);
      setSuggestions(data.slice(0, 5)); // limit to top 5
    } catch (err) {
      console.error("Semantic search error:", err);
      setSuggestions([]);
      setSearchError("Semantic search is currently unavailable. Try Keyword search instead.");
    }
  };

  const handleSuggestionClick = async (course) => {
    const courseCodes = Array.isArray(course.course_codes)
      ? course.course_codes
      : [course.course_codes];

    setShowSuggestions(false);

    // Looking up a course brings up its popup — it does NOT select/highlight it.
    // (Searching for a course doesn't mean the user wants to take it.)
    setSelectedCourse(course);

    setInput(""); // Clear the input after search
  };

  // Function to handle course selection (adding/removing from highlighted list)
  const handleAddSelectedCourse = async (course) => {
    const courseCodes = Array.isArray(course.course_codes)
      ? course.course_codes
      : [course.course_codes];

    const primaryCode = courseCodes[0];

    // Check if already highlighted
    const currentHighlighted = Array.isArray(highlighted) ? highlighted : [];
    const isAlreadyHighlighted = currentHighlighted.includes(primaryCode) ||
      courseCodes.some(code => currentHighlighted.includes(code)) ||
      currentHighlighted.includes(courseCodes.join('/'));

    let newHighlighted;
    if (isAlreadyHighlighted) {
      // Remove from highlighted
      newHighlighted = currentHighlighted.filter(code =>
        !courseCodes.includes(code) && code !== courseCodes.join('/')
      );
    } else {
      // Add to highlighted
      newHighlighted = [...currentHighlighted, primaryCode];
    }

    // Update highlighted courses
    onHighlight(newHighlighted);

    // Check for conflicts
    try {
      const response = await fetch(`${backendUrl}/conflicted_courses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          taken_courses: newHighlighted,
          semester: currentSemester
        }),
      });
      const data = await response.json();
      onConflicted(data.conflicted_courses);
    } catch (error) {
      console.error('Error checking conflicts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (useSemanticSearch) {
      // Trigger semantic search on submit
      await searchSemanticCourse(input);
      setShowSuggestions(true); // Show semantic search results
      return;
    }

    const codes = input
      .split(",")
      .map((code) => code.trim().toUpperCase())
      .filter((code) => code.length > 0);

    if (codes.length === 0) return; // nothing to process

    // Create a set of all valid course codes
    const allCourseCodes = new Set(
      allCourses.flatMap(course =>
        Array.isArray(course.course_codes)
          ? course.course_codes
          : [course.course_codes]
      )
    );

    // Filter input codes to only keep valid ones
    const validCodes = codes.filter(code => allCourseCodes.has(code));

    if (validCodes.length === 0) return; // no valid codes, do nothing

    // Combine with current highlighted courses
    const currentHighlighted = Array.isArray(highlighted) ? highlighted : [];
    const newHighlighted = [...currentHighlighted, ...validCodes];
    onHighlight(newHighlighted);

    // Check for conflicts
    try {
      const response = await fetch(`${backendUrl}/conflicted_courses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          taken_courses: newHighlighted,
          semester: currentSemester
        }),
      });
      const data = await response.json();
      onConflicted(data.conflicted_courses);
    } catch (error) {
      console.error('Error checking conflicts:', error);
    }

    setInput(""); // Clear the input after search
  };

  // Update suggestions when semester changes
  useEffect(() => {
    if (input.trim()) {
      searchCourses(input);
    } else {
      // Clear suggestions when semester changes and there's no input
      setSuggestions([]);
    }
  }, [currentSemester, input]);

  return (
    <div className="relative w-full max-w-[560px]">
      <form
        onSubmit={handleSubmit}
        className="search-pill flex items-center gap-2"
      >
        {/* Segmented Keyword / Semantic toggle — both visible, active one filled */}
        <div className="inline-flex shrink-0 rounded-full border border-line bg-surface-alt p-0.5">
          <button
            type="button"
            onClick={() => { setUseSemanticSearch(false); setSearchError(null); }}
            className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
              !useSemanticSearch ? "bg-surface text-primary shadow-sm" : "text-ink-soft hover:text-ink"
            }`}
            title="Match by course code or title"
          >Keyword</button>
          <button
            type="button"
            onClick={() => { setUseSemanticSearch(true); setSearchError(null); }}
            className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
              useSemanticSearch ? "bg-surface text-primary shadow-sm" : "text-ink-soft hover:text-ink"
            }`}
            title="Match by meaning — describe the idea"
          >
            <Sparkles className="h-3 w-3" />
            Semantic
          </button>
        </div>

        {/* Search input */}
        <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-full border border-line bg-surface-alt px-3 transition-colors focus-within:border-line-strong focus-within:bg-surface focus-within:shadow-focus">
          <SearchIcon className="h-4 w-4 shrink-0 text-ink-soft" />
          <input
            id="course-input"
            type="text"
            value={input}
            onChange={handleInputChange}
            onFocus={() => setShowSuggestions(true)}
            placeholder={
              useSemanticSearch
                ? "Describe what you want to learn…"
                : "Search by course code or title"
            }
            className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-soft"
          />
          {input && (
            <button type="button" onClick={() => { setInput(""); setSearchError(null); }} className="shrink-0 text-ink-soft hover:text-ink" aria-label="Clear">×</button>
          )}
        </div>
      </form>

      {showSuggestions && input.trim() && (
        <div className="absolute z-50 mt-1.5 w-full overflow-hidden rounded-xl border border-line bg-surface shadow-lift">
          {searchError ? (
            <div className="px-4 py-3 text-sm text-ink-muted">
              <div>{searchError}</div>
              <button
                type="button"
                onClick={() => { setUseSemanticSearch(false); setSearchError(null); }}
                className="mt-2 text-xs font-semibold text-primary hover:underline"
              >Switch to Keyword search</button>
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((course, index) => (
              <div
                key={index}
                onClick={() => handleSuggestionClick(course)}
                className="cursor-pointer border-b border-line px-4 py-2.5 text-sm transition-colors last:border-0 hover:bg-surface-alt"
              >
                <div className="font-medium text-primary">
                  {Array.isArray(course.course_codes)
                    ? course.course_codes.join(", ")
                    : course.course_codes}
                </div>
                <div className="text-ink-muted">{course.course_title}</div>
                {course.semesters && course.semesters.length > 1 && (
                  <div className="mt-1 text-xs text-ink-soft">
                    Taught in: {course.allSemestersText}
                  </div>
                )}
                {course.semesters && course.semesters.length === 1 && (
                  <div className="mt-1 text-xs text-ink-soft">{course.semester}</div>
                )}
                {!course.semesters && course.semester && (
                  <div className="mt-1 text-xs text-ink-soft">{course.semester}</div>
                )}
              </div>
            ))
          ) : (
            <div className="px-3 py-4 text-center text-sm italic text-ink-soft">
              No matching courses
            </div>
          )}
        </div>
      )}

      {/* Portal-render CoursePopup to document.body so it escapes
          the navbar stacking context and always appears above the graph */}
      {selectedCourse && createPortal(
        <CoursePopup
          course={selectedCourse}
          onClose={() => setSelectedCourse(null)}
          onHighlight={onHighlight}
          highlighted={highlighted}
          activeTab="thisSemester"
          onSelect={handleAddSelectedCourse}
          courseDetailsData={allCourses}
          setSelectedCourse={setSelectedCourse}
        />,
        document.body
      )}

    </div>
  );
}
